import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getDeviceState, selectDeviceState, selectDeviceStateError, resetDeviceStateRetryCount, setDeviceStateError } from './deviceStateSlice';
import { selectIntersectionState, setHudDimensions } from '../map/mapSlice';
import { filterSignalsByHeading } from '../trafficInfra/SignalMetadata';
import Signal from './Signal';
import { isDriverSignal, isPrioritySignal } from './SignalGroup';

import styles from './TrafficLightHUD.module.css';

const TrafficLightHUD = ({ activeLight, distance }) => {
    const dispatch = useDispatch();
    const deviceState = useSelector(selectDeviceState);
    const error = useSelector(selectDeviceStateError);
    const { entryHeading, exitHeading } = useSelector(selectIntersectionState);
    const { t } = useTranslation();

    const hudRef = React.useRef(null);

    // Polling logic
    useEffect(() => {
        if (!activeLight) return;
        const deviceId = activeLight.liva_nro;

        // Reset state for new light
        dispatch(setDeviceStateError(null));
        dispatch(resetDeviceStateRetryCount());

        const fetchState = () => dispatch(getDeviceState(deviceId));
        fetchState(); // Initial fetch

        const interval = setInterval(fetchState, 1000);
        return () => clearInterval(interval);
    }, [activeLight, dispatch]);

    // Measure HUD dimensions
    useEffect(() => {
        if (!hudRef.current) return;

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                dispatch(setHudDimensions({ width, height }));
            }
        });

        resizeObserver.observe(hudRef.current);

        return () => {
            resizeObserver.disconnect();
            // Optional: reset dimensions on unmount? 
            // dispatch(setHudDimensions({ width: 0, height: 0 })); 
            // But checking unmount might be tricky if it just hides. 
            // The HUD component is conditionally rendered in MapView, so unmount works.
            dispatch(setHudDimensions({ width: 0, height: 0 }));
        };
    }, []);

    if (!activeLight) return null;

    const allSignals = deviceState?.signalGroup || [];
    const filteredSignals = filterSignalsByHeading(allSignals, activeLight.liva_nro, entryHeading, exitHeading);
    const vehicleSignals = filteredSignals.filter(s => isDriverSignal(s) && !isPrioritySignal(s));
    const prioritySignals = filteredSignals.filter(s => isPrioritySignal(s));

    return (
        <div className={styles.TrafficLightHUD} ref={hudRef}>
            <h3 className={styles.Title}>{activeLight.paikka}</h3>
            <div className={styles.Distance}>
                {Math.round(distance)} m
            </div>

            {error ? (
                <div className={styles.Error}>{t('hud.connection_error') || 'Connection Error'}</div>
            ) : (
                <div className={styles.SignalsContainer}>
                    {/* Vehicle Signals Row */}
                    <div className={styles.SignalsRow}>
                        {vehicleSignals.length > 0 ? (
                            vehicleSignals.map(signal => (
                                <Signal key={signal.idx} signal={signal} />
                            ))
                        ) : (
                            <div className={styles.Loading}>{t('hud.loading') || 'Loading signals...'}</div>
                        )}
                    </div>

                    {/* Priority Signals Row */}
                    {prioritySignals.length > 0 && (
                        <div className={styles.PriorityRow}>
                            {prioritySignals.map(signal => (
                                <Signal key={signal.idx} signal={signal} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TrafficLightHUD;
